import "@formatjs/intl-numberformat/polyfill";
import "@formatjs/intl-numberformat/locale-data/en";
import { v4 as uuidv4 } from "uuid";
import { metaCurrenciesWithDecimals } from "./constants";

export const numberCurrencyFormat = (number = 0) => {
  number = number || 0;
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

export const numberCompactFormat = (number) =>
  new Intl.NumberFormat("en", { notation: "compact" }).format(number);

// Magnitude-based: compact for large numbers (>= 1000), decimals for smaller
export const numberMetricFormat = (number) => {
  const num = typeof number === "number" ? number : parseFloat(number);
  if (isNaN(num) || !isFinite(num)) return String(number);
  return Math.abs(num) >= 1000
    ? numberCompactFormat(num)
    : num.toLocaleString("en", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
};

export const numberDecimal = (number, decimal = 2) => {
  if (typeof number !== "number") return number;
  return Math.round((number + Number.EPSILON) * 10 ** decimal) / 10 ** decimal;
};

/**
 * True when a metric should participate in averages or per-currency sums where
 * null, empty, 0 and "0" mean “no data” (e.g. ROAS, CPA, spend lines).
 */
export const isMeaningfulNumericMetricValue = (value) => {
  if (value == null || value === "") return false;
  if (value === 0 || value === "0") return false;
  const n = Number(value);
  return !Number.isNaN(n);
};

export const numberRounded = (number) => {
  if (typeof number !== "number") return number;
  return Math.round(number);
};
export const numberRoundedToGreater = (number) => {
  if (typeof number !== "number") return number;
  return Math.ceil(number);
};

/**
 * Budget amounts for Meta: two decimals when the currency supports them, otherwise whole units.
 */
export const normalizeMetaBudgetAmount = (value, currency) => {
  const num = Number(value);
  if (isNaN(num) || !isFinite(num)) return num;
  if (metaCurrenciesWithDecimals.includes(currency)) {
    return numberDecimal(num, 2);
  }
  return Math.round(num);
};

/**
 * Meta budget limits in percentage mode: whole percent only (0–100), no decimals.
 */
export const clampIntegerMetaBudgetPercent = (value) => {
  const n = Math.round(Number(value));
  if (isNaN(n) || !isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

/**
 * Currency amount from an integer percent of campaign budget (Meta percentage rules).
 */
export const metaBudgetAmountFromIntegerPercent = (
  integerPercent,
  campaignBudget,
  currency
) => {
  const pct = clampIntegerMetaBudgetPercent(integerPercent);
  const budget = Number(campaignBudget) || 0;
  const raw = (pct / 100) * budget;
  return normalizeMetaBudgetAmount(raw, currency);
};

export function textNumberCompactFormat(value) {
  return `${numberCompactFormat(value)}`;
}

export const getUniqueID = () => uuidv4();

export const idsMatch = (left, right) => {
  if (left == null || right == null || left === "" || right === "") {
    return false;
  }
  return String(left) === String(right);
};

export const entityMatchesId = (entity, lookupId) =>
  Boolean(entity) &&
  (idsMatch(entity.id, lookupId) || idsMatch(entity.idPlatform, lookupId));
